import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeCardSelector — Icon + label card grid for single-choice selection.
 * Compact card layout for mode or view type pickers.
 *
 * Usage:
 *   <NodeCardSelector
 *     label="View Type"
 *     value={viewType}
 *     onChange={setViewType}
 *     options={[
 *       { value: 'table', label: 'Table', icon: TableRowsIcon },
 *       { value: 'kanban', label: 'Kanban', icon: ViewKanbanIcon, description: '...' },
 *     ]}
 *     descriptive
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup, {
  shouldForwardProp: (prop) => prop !== 'descriptive' && prop !== 'compact',
})(({ descriptive, compact }) => ({
  display: descriptive ? 'grid' : 'flex',
  gridTemplateColumns: descriptive ? 'repeat(2, minmax(0, 1fr))' : undefined,
  flexWrap: descriptive ? undefined : 'wrap',
  gap: descriptive ? (compact ? '6px' : '8px') : '6px',
  width: '100%',
  '& .MuiToggleButtonGroup-grouped': {
    margin: 0,
    border: 'none',
    borderRadius: `${NODE_UI.borderRadius} !important`,
  },
}));

const CardButton = styled(ToggleButton, {
  shouldForwardProp: (prop) => prop !== 'descriptive' && prop !== 'compact',
})(({ theme, descriptive, compact }) => {
  const compactDescriptive = descriptive && compact;
  return {
  flex: descriptive ? undefined : '0 0 auto',
  minWidth: descriptive ? 0 : 70,
  maxWidth: descriptive ? 'none' : 85,
  width: descriptive ? '100%' : undefined,
  display: 'flex',
  flexDirection: compactDescriptive ? 'row' : 'column',
  alignItems: compactDescriptive ? 'flex-start' : (descriptive ? 'flex-start' : 'center'),
  gap: compactDescriptive ? 6 : (descriptive ? 4 : 3),
  padding: compactDescriptive ? '6px 8px' : (descriptive ? '10px 10px' : '8px 6px'),
  border: `${NODE_UI.border(theme)} !important`,
  borderRadius: `${NODE_UI.borderRadius} !important`,
  textTransform: 'none',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: `${theme.palette.text.secondary} !important`,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    borderColor: `${theme.palette.text.disabled} !important`,
    color: theme.palette.text.primary,
    '& .node-card-selector__description': {
      color: theme.palette.text.secondary,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  '&.Mui-disabled': {
    opacity: 0.45,
  },
  '& .MuiSvgIcon-root': {
    fontSize: compactDescriptive ? 16 : (descriptive ? 18 : 20),
    flexShrink: 0,
    marginTop: compactDescriptive ? 1 : 0,
  },
  '& .node-card-selector__text': {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: compactDescriptive ? 1 : undefined,
    width: compactDescriptive ? undefined : '100%',
    gap: compactDescriptive ? 1 : 0,
  },
  '& .node-card-selector__label': {
    fontSize: compactDescriptive ? NODE_UI.fontSize.caption : (descriptive ? NODE_UI.fontSize.value : NODE_UI.fontSize.caption),
    fontWeight: descriptive ? 600 : NODE_UI.fontWeight.label,
    lineHeight: 1.2,
    textAlign: compactDescriptive ? 'left' : (descriptive ? 'left' : 'center'),
    width: compactDescriptive ? undefined : '100%',
  },
  '& .node-card-selector__description': {
    fontSize: compactDescriptive ? '10px' : NODE_UI.fontSize.caption,
    fontWeight: NODE_UI.fontWeight.value,
    lineHeight: 1.3,
    textAlign: 'left',
    color: theme.palette.text.secondary,
    width: '100%',
    display: '-webkit-box',
    WebkitLineClamp: compactDescriptive ? 1 : 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
};
});

const NodeCardSelector = React.memo(function NodeCardSelector({
  label,
  value,
  onChange,
  options = [],
  disabled = false,
  descriptive = false,
  /** Tighter descriptive cards — icon beside label, single-line description. */
  compact = false,
  /** See NodeSelect `canvas` — nodrag/nopan on React Flow canvas nodes. */
  canvas = false,
  sx,
}) {
  const handleChange = useCallback((_event, nextValue) => {
    if (nextValue != null) {
      onChange?.(nextValue);
    }
  }, [onChange]);

  return (
    <div
      className={canvas ? 'nodrag nopan nowheel' : undefined}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {label && <Label>{label}</Label>}
      <StyledToggleButtonGroup
        descriptive={descriptive}
        compact={compact}
        value={value ?? ''}
        exclusive
        onChange={handleChange}
        disabled={disabled}
        sx={sx}
      >
        {options.map((opt) => {
          const Icon = opt.icon;
          const ariaLabel = opt.description
            ? `${opt.label}. ${opt.description}`
            : opt.label;
          const useCompactLayout = descriptive && compact;
          return (
            <CardButton
              key={opt.value}
              value={opt.value}
              descriptive={descriptive}
              compact={compact}
              disabled={disabled || opt.disabled}
              aria-label={ariaLabel}
            >
              {useCompactLayout ? (
                <>
                  {Icon && <Icon fontSize="small" />}
                  <span className="node-card-selector__text">
                    <span className="node-card-selector__label">{opt.label}</span>
                    {opt.description ? (
                      <span className="node-card-selector__description">{opt.description}</span>
                    ) : null}
                  </span>
                </>
              ) : (
                <>
                  {Icon && <Icon fontSize="small" />}
                  <span className="node-card-selector__label">{opt.label}</span>
                  {descriptive && opt.description ? (
                    <span className="node-card-selector__description">{opt.description}</span>
                  ) : null}
                </>
              )}
            </CardButton>
          );
        })}
      </StyledToggleButtonGroup>
    </div>
  );
});

export default NodeCardSelector;
